// ⚠️ FICHIER ENGENDRÉ — NE PAS ÉDITER À LA MAIN.
// Source : lib/operateur-leviers.js + lib/leviers/*.js
// Régénérer : node scripts/generer-miroir-leviers.js
// Pourquoi ce doublon : CRA/webpack refuse les imports hors de src/ (Jest, lui,
// les accepte — d'où le risque de tests verts sur un build cassé).
// lib/leviers/stock.js — LES LEVIERS DU STOCK, SERVIS PAR `/api/stock`.
//
// ══ LE JALON (2026-09-16, chantier « les écrans où il n'a aucune porte »,
// lot 5) ═══════════════════════════════════════════════════════════════════
//
// PREMIÈRE FAMILLE HORS `/api/beds24-mapping`. Jusqu'ici, une seule ligne de
// `leviers/_forme.js` (`endpoint !== "/api/beds24-mapping"`) fermait les cinq
// écrans dont la porte est « sur une autre rue » — dont le stock. Décision
// Dimitri : « oui, sur le stock d'abord ». L'endpoint est le plus proche du
// routeur par sa forme (un `action` en corps, une garde de rôle, un switch), et
// ses cinq actions existent, sont testées, et n'attendaient qu'une liste
// blanche.
//
// ⚠️ CE QUI EST PROPRE À CET ENDPOINT, ET QUE LE LEVIER DOIT SAVOIR :
//   * il ne répond pas `{ ok, audit_id }` mais l'instantané complet du stock
//     (un 200 vaut « fait ») et `{ error }` sur refus — `executerGeste` lit ce
//     verdict-là sans rien affaiblir de l'autre ;
//   * un achat ou un dépôt y est un DOCUMENT (identifiant, unités dérivées du
//     conditionnement, coût figé). L'opérateur compose ce que Dimitri DIT ; la
//     forme dictée (sans identifiant) est transformée côté serveur par
//     `lib/stock-dicte.js` avec les formules des modales de l'écran — jamais un
//     identifiant inventé, jamais un coût deviné ;
//
// ⚠️ ET L'UNITÉ SE DIT, ELLE NE SE DEVINE PAS (revue adverse #1449, CRITIQUE 1,
// 2026-09-16). Les champs du document sont en unités PRÉLEVABLES — jamais en
// rouleaux. `cs9` « Papier toilette » en prod : carton de 48, sous-paquets de
// 6, 8,62 € le carton. « J'ai déposé 6 rouleaux » composé tel quel sortait 6
// sous-paquets (36 rouleaux, 6,47 €) là où la réalité est 1 sous-paquet et
// 1,08 € ; « 20 rouleaux à 12 € » ajoutait 160 sous-paquets, soit 960
// rouleaux. Un facteur 6 à 48, sur le stock ET sur l'euro — et sur les phrases
// que ce fichier donnait lui-même en exemple. D'où la clé `unite`, OBLIGATOIRE
// et à liste fermée (`paquet` / `unite_prelevable` / `unite_finale`) : le
// modèle dit dans quelle unité il compte, le serveur convertit, et la carte
// affiche la quantité CONVERTIE avec son coût avant le clic.
//   * l'aperçu (`stock_article_etat_lire`) est servi par `/api/stock` aussi :
//     quantité, seuil, dernier mouvement — et le prix, à l'admin seulement.
//
// ⚠️ CE QUI N'EST PAS ICI. `stock_signaler` vit dans `menage.js` : c'est une
// action du ROUTEUR (`lib/stock-signalements-handlers.js`), pas de cet
// endpoint. Deux familles, deux guichets, zéro doublon — l'agrégation le
// vérifie.
//
// ⚠️ VAGUE 5, FERMÉE PAR DÉFAUT. Ces gestes ne sortent jamais de FBZ, mais ils
// touchent un COMPTAGE, et deux d'entre eux une DÉPENSE : rien de comparable
// dans les vagues déjà vérifiées. `OPERATEUR_GESTES_VAGUE=5:` les rend
// candidats, `5:stock_article_regler` en ouvre un seul.

const LEVIERS_STOCK = Object.freeze({
  // ══ « BAISSE LE SEUIL DES SERVIETTES À 6 » ═══════════════════════════════
  //
  // Action `stock_set` : admin seul, `qtyDispo` et/ou `seuilMin`, borné à
  // zéro. Un COMPTAGE, pas un montant : aucun marquage.
  stock_article_regler: {
    ecran: "stock",
    endpoint: "/api/stock",
    action: "stock_set",
    args_autorises: ["csId", "qtyDispo", "seuilMin"],
    // ⚠️ L'ARTICLE SE DIT PAR SON NOM (lot 4). Le modèle n'a jamais vu un
    // `cs9` : la lecture `stock_etat` rend des noms. Il pose donc
    // `vise:{nom:"papier toilette"}` et `lib/stock-repere.js` retrouve
    // l'identifiant. `csId` RESTE dans `args_autorises` (sinon `filtrerArgs`
    // refuse la carte au clic, et l'aperçu ne pourrait plus le citer).
    args_resolus: {
      csId: { objet: "consommable", par: ["nom"] },
    },
    apercu: {
      // ⚠️ LA NOUVELLE VALEUR VOYAGE AVEC LA LECTURE (revue adverse #1449) :
      // `qtyDispo` est en unités PRÉLEVABLES, et la carte doit afficher « 40
      // sous-paquets de 6, soit 240 à l'unité » AVANT le clic — sinon Dimitri
      // valide un comptage dont il ne connaît pas l'unité.
      lecture: "stock_article_etat_lire",
      depend_de: ["csId", "qtyDispo", "seuilMin"],
      jeton: "x-apercu-version",
    },
    titre: "Régler la quantité en stock ou le seuil d'alerte d'un article",
    constatable:
      "dans Stock › Stock central, l'article affiche la nouvelle quantité et/ou le nouveau seuil d'alerte avec l'heure de mise à jour ; l'alerte « sous le seuil » s'allume ou s'éteint aussitôt selon le nouveau rapport entre les deux",
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour:
      "L'ancienne quantité et l'ancien seuil ne sont gardés nulle part : pour revenir en arrière, il faudra les retaper.",
    ne_fait_pas:
      "il n'achète rien, ne dépose rien et ne prévient personne : c'est une correction de comptage sur le stock central, en unités de distribution (des sous-paquets de 6, pas des rouleaux) — le sac des agents et les dépôts déjà saisis ne bougent pas",
    // ⚠️ DES ARTICLES QUI EXISTENT (revue adverse M4) : « serviettes » et
    // « draps » ne sont pas au catalogue — un exemple donné au modèle vaut
    // consigne, et il aurait promis un article que le résolveur refuse. Les
    // trois phrases passent le test des 25 noms réels.
    dit_comme: [
      "Baisse le seuil des éponges à 6",
      "Mets le stock de papier toilette à 40 sous-paquets, j'ai recompté",
      "Il reste 12 lessives en réalité, corrige le stock",
    ],
    vague: 5,
    motif_requis: false,
    autonome: false,
  },

  // ══ « NOTE QUE J'AI ACHETÉ 20 ROULEAUX À 12 € » ═════════════════════════
  //
  // Action `achat_create` : admin seul. Forme DICTÉE (`lib/stock-dicte.js`) :
  // l'article, la date, ce qui a été acheté (`quantite` + `unite`), le prix
  // payé — les unités ajoutées et le prix unitaire se DÉRIVENT du
  // conditionnement au catalogue, avec la formule partagée avec
  // `SaisirAchatModal`. `unite` dit si « 3 » compte des cartons, des
  // sous-paquets ou des rouleaux : sans elle, 3 cartons et 3 rouleaux
  // écriraient le même chiffre.
  //
  // ⚠️ ARGENT : le montant payé entre dans les dépenses du mois (Compta). La
  // carte le montre avant le clic — c'est le marquage qui l'exige.
  stock_achat_enregistrer: {
    ecran: "stock",
    endpoint: "/api/stock",
    action: "achat_create",
    args_autorises: ["achat"],
    // ⚠️ L'ARTICLE VIT DANS L'ACHAT — notation pointée (lot 4, voie A) : le
    // résolveur pose `args.achat.csId` sans écraser la date, la quantité et
    // le prix que le modèle a composés. `csId` sort de `cles_requises` : c'est
    // le repère (`vise=nom`) qui l'exige désormais, et `produitDuCatalogue`
    // reste le filet au clic (400 « Article inconnu »).
    args_resolus: {
      "achat.csId": { objet: "consommable", par: ["nom"] },
    },
    // ⚠️ LE PRIX PAYÉ SE TAPE SUR LA CARTE, IL NE SE COMPOSE PAS (revue #1501,
    // M4). Le modèle l'entendait (« à 17 € »), l'écrivait, et l'opérateur
    // l'énonçait comme un fait ; sans prix dit, le geste tombait en silence.
    // Désormais : retiré s'il est composé, champ sur la carte, bouton gris tant
    // qu'il est vide, posé par geste-executer avec ce que Dimitri a tapé.
    args_saisis: {
      "achat.prixTotal": {
        libelle: "le prix payé, en euros",
        nature: "montant",
      },
    },
    args_formes: {
      achat: {
        cles: [
          "csId",
          "date",
          "quantite",
          "unite",
          "prixTotal",
          "enseigne",
          "notes",
        ],
        // ⚠️ SANS L'UNITÉ, LA QUANTITÉ NE VEUT RIEN DIRE (revue #1449). Elle
        // remplace l'ancien `mode` A/P, qui disait la même chose à moitié et
        // que le modèle ne pouvait pas déduire d'une phrase.
        // `prixTotal` n'y est plus : il se TAPE sur la carte (`args_saisis`).
        cles_requises: ["date", "quantite", "unite"],
        cles_valeurs: {
          unite: ["paquet", "unite_prelevable", "unite_finale"],
        },
        // ⚠️ LE SILENCE MESURÉ EN PROD LE 23/09. « Enregistre un achat de 2
        // cartons de papier toilette » : le modèle pose l'unité qu'il a
        // ENTENDUE — « carton » —, elle n'est pas dans la liste fermée,
        // `lireGeste` rend `null`, et Dimitri lit « C'est prêt : 2 de papier
        // toilette » devant un écran SANS CARTE. L'unité reste refusée ; son
        // refus se dit.
        cles_valeurs_refus: {
          unite:
            "Je compte en paquets, en sous-paquets ou à l'unité — « {dit} », je ne sais pas le convertir. Redites-le-moi dans l'une de ces trois façons de compter et je le prépare.",
        },
        texte: true,
      },
    },
    apercu: {
      lecture: "stock_article_etat_lire",
      depend_de: ["achat"],
      jeton: "x-apercu-version",
    },
    titre: "Enregistrer un achat de consommables",
    constatable:
      "l'achat apparaît dans Stock › Achats à sa date, avec l'enseigne et le montant en € ; le stock central de l'article augmente du nombre d'unités que ce conditionnement représente, et la dépense du mois en Compta monte d'autant",
    sujet: "terrain",
    cout: ["argent"],
    ou_ca_part: ["personne"],
    marquages: ["argent"],
    sans_retour:
      "Passé la fin du mois, cet achat ne pourra plus être retiré : sa dépense reste dans la compta du mois.",
    ne_fait_pas:
      "il ne commande rien et ne paie rien : il note un achat déjà fait. Les unités ajoutées viennent du conditionnement au catalogue — un carton de 48 rouleaux en sous-paquets de 6 ajoute 8 unités, pas 48",
    // Trois phrases jouées sur les 25 noms réels (revue adverse M4) : l'article
    // existe, l'unité se dit, la quantité tombe juste (12 rouleaux = 2
    // sous-paquets de 6).
    // ⚠️ SANS MONTANT (revue #1501 v2) : le prix se tape sur la carte, et le
    // registre ne promet pas ce que le code retire aussitôt.
    dit_comme: [
      "Enregistre un achat de 2 cartons de papier toilette",
      "Note que j'ai acheté 4 gels douche",
      "J'ai acheté 12 rouleaux de papier toilette hier",
    ],
    vague: 5,
    motif_requis: false,
    autonome: false,
  },

  // ══ « RETIRE L'ACHAT D'HIER, JE L'AI SAISI DEUX FOIS » ══════════════════
  //
  // Action `achat_delete` : admin seul, refusée si le mois de l'achat est clos.
  // L'aperçu lit le mouvement par son identifiant : la carte montre QUEL achat
  // (date, quantité, montant) avant que Dimitri ne l'efface.
  stock_achat_retirer: {
    ecran: "stock",
    endpoint: "/api/stock",
    action: "achat_delete",
    args_autorises: ["id"],
    // ⚠️ L'ACHAT SE DÉSIGNE PAR L'ARTICLE ET LE JOUR (lot 4). Le modèle n'a
    // jamais vu un « ach_… » : s'il en pose un, il l'a inventé, et c'est un
    // autre achat qui s'efface. `rang` répond à « lequel ? » au second tour.
    args_resolus: {
      id: {
        objet: "mouvement_stock",
        par: ["nom", "quand", "rang"],
        requis: ["nom", "quand"],
      },
    },
    apercu: {
      lecture: "stock_article_etat_lire",
      depend_de: ["id"],
      jeton: "x-apercu-version",
    },
    titre: "Retirer un achat de consommables saisi par erreur",
    constatable:
      "l'achat disparaît de Stock › Achats, son montant en € sort des dépenses du mois en Compta, et le stock central de l'article rebaisse du nombre d'unités qu'il avait ajouté (jamais sous zéro)",
    sujet: "terrain",
    cout: ["argent"],
    ou_ca_part: ["personne"],
    marquages: ["supprime", "argent"],
    sans_retour:
      "L'achat effacé ne se restaure pas : il faudra le ressaisir (date, quantité, prix) si c'était une erreur.",
    ne_fait_pas:
      "il ne rembourse rien et ne touche pas aux dépôts déjà faits avec ces unités : si elles ont été distribuées entre-temps, le stock central s'arrête à zéro sans reprendre ce qui est parti",
    // ⚠️ « le dernier achat » SANS JOUR ne désigne rien (Q1 du cadrage,
    // ouverte) : l'opérateur demande le jour. « serviettes » n'existe pas.
    dit_comme: [
      "Retire l'achat de papier toilette d'hier, je l'ai saisi deux fois",
      "Cet achat de café est une erreur, enlève-le",
      "Retire l'achat de lessive de lundi dernier",
    ],
    vague: 5,
    motif_requis: false,
    autonome: false,
  },

  // ══ « NOTE QUE J'AI DÉPOSÉ 6 ROULEAUX À COCON » ═════════════════════════
  //
  // Action `depot_upsert` : admin ou orchestrateur (borné à ses logements et au
  // mois ouvert — la garde de rôle reste dans `api/stock.js`) ; le stock central
  // est débité dans tous les cas, l'agent de ménage n'y a aucun accès. Forme DICTÉE : UN article, UN logement,
  // une quantité DANS SON UNITÉ (`quantite` + `unite`) ; le coût se calcule au
  // prix catalogue avec la formule de `SaisirDepotModal`, et s'impute au
  // logement en Compta. La carte affiche la quantité convertie ET le coût
  // avant le clic — l'euro écrit ne s'apprend jamais après coup.
  //
  // ⚠️ UN SEUL ARTICLE PAR GESTE, ASSUMÉ : une liste de lignes ne tient pas
  // dans la forme fermée des arguments, et l'élargir pour un cas ouvrirait
  // une porte de service. Deux articles = deux gestes, chacun relu.
  stock_depot_enregistrer: {
    ecran: "stock",
    endpoint: "/api/stock",
    action: "depot_upsert",
    args_autorises: ["depot"],
    // ⚠️ LE LOGEMENT ET L'ARTICLE VIVENT DANS LE DÉPÔT — notation pointée
    // (lot 4). Le logement d'ABORD : « ajoute 10 rouleaux au Cocon » pose
    // d'abord la question « quel Cocon ? », puis celle de l'article — une
    // question à la fois. Et `gardesDuStock` lit `args.depot.logId` déjà posé
    // pour nommer le logement dans la phrase. `logId` et `csId` sortent de
    // `cles_requises` : ce sont les repères qui les exigent.
    args_resolus: {
      "depot.logId": { objet: "logement", par: ["logement"] },
      "depot.csId": { objet: "consommable", par: ["nom"] },
    },
    args_formes: {
      depot: {
        cles: [
          "logId",
          "date",
          "csId",
          "quantite",
          "unite",
          "initiales",
          "notes",
        ],
        // ⚠️ L'UNITÉ EST OBLIGATOIRE (revue #1449) : « 6 rouleaux » composé
        // sur l'ancien `nbPrelevables` écrivait 6 sous-paquets — 36 rouleaux
        // et 6,47 € au lieu de 1 et 1,08 €.
        cles_requises: ["date", "quantite", "unite"],
        cles_valeurs: {
          unite: ["paquet", "unite_prelevable", "unite_finale"],
        },
        // ⚠️ MÊME SILENCE, MÊME JOUR (prod du 23/09). « Dépose 10 rouleaux de
        // papier toilette au Cocon Naturel » : « rouleau » n'est pas une des
        // trois façons de compter, la carte tombait sans un mot — et le refus
        // qui aurait dû se lire (« 10 à l'unité ne font pas un nombre entier
        // de sous-paquet de 6 ») n'était même pas atteint.
        cles_valeurs_refus: {
          unite:
            "Je compte en paquets, en sous-paquets ou à l'unité — « {dit} », je ne sais pas le convertir. Redites-le-moi dans l'une de ces trois façons de compter et je le prépare.",
        },
        texte: true,
      },
    },
    apercu: {
      lecture: "stock_article_etat_lire",
      depend_de: ["depot"],
      jeton: "x-apercu-version",
    },
    titre: "Enregistrer un dépôt de consommables dans un logement",
    constatable:
      "le dépôt apparaît dans Stock › Dépôts à sa date, pour ce logement, avec sa quantité et son coût en € (quantité × prix catalogue de l'unité) ; le stock central de l'article baisse d'autant, et un manque signalé sur cet article pour ce logement se ferme tout seul",
    sujet: "terrain",
    cout: ["argent"],
    ou_ca_part: ["personne"],
    marquages: ["argent"],
    sans_retour:
      "Passé la fin du mois, ce dépôt ne pourra plus être retiré ni corrigé d'ici : son coût reste imputé au logement.",
    ne_fait_pas:
      "il ne dépose qu'un seul article par geste et ne prévient personne. La quantité dite est convertie au conditionnement : 6 rouleaux de papier toilette font 1 sous-paquet de 6, pas 6. Il débite le stock central",
    // Quatre phrases sur des articles ET des logements réels, non ambigus
    // (revue adverse M4 : « à Cocon » désignait lc1 ET l5, « au Nid » et « 2
    // draps » n'existent pas). Jouées sur les 25 noms et sur le parc.
    dit_comme: [
      "Note que j'ai déposé 6 rouleaux de papier toilette au Cocon Naturel",
      "J'ai laissé 2 sous-paquets de papier toilette à Sur un Nuage",
      "J'ai remis 4 gels douche à Soleil du Sud",
      "Ajoute un dépôt de 2 flacons de gel wc au Havre de Bruguières hier",
    ],
    vague: 5,
    motif_requis: false,
    autonome: false,
  },

  // ══ « CE DÉPÔT EST EN DOUBLE, ENLÈVE-LE » ═══════════════════════════════
  //
  // Action `depot_delete` : admin, ou ménage / orchestrateur sur SON dépôt
  // d'un logement accessible, mois ouvert. Idempotente côté store (un dépôt
  // déjà parti ne lève pas) — mais l'aperçu, lui, dit « n'existe plus ».
  stock_depot_retirer: {
    ecran: "stock",
    endpoint: "/api/stock",
    action: "depot_delete",
    args_autorises: ["id"],
    // Un dépôt se désigne par l'article, le LOGEMENT et le jour (lot 4) ; un
    // dépôt à plusieurs lignes part en entier, la garde nomme les autres.
    args_resolus: {
      id: {
        objet: "mouvement_stock",
        par: ["nom", "logement", "quand", "rang"],
        requis: ["nom", "logement", "quand"],
      },
    },
    apercu: {
      lecture: "stock_article_etat_lire",
      depend_de: ["id"],
      jeton: "x-apercu-version",
    },
    titre: "Retirer un dépôt de consommables saisi par erreur",
    constatable:
      "le dépôt disparaît de Stock › Dépôts, son coût en € sort des charges du logement en Compta, et le stock central de l'article remonte de la quantité déposée",
    sujet: "terrain",
    cout: ["argent"],
    ou_ca_part: ["personne"],
    marquages: ["supprime", "argent"],
    sans_retour:
      "Le dépôt effacé ne se restaure pas : il faudra le ressaisir (logement, date, quantité) si c'était une erreur.",
    ne_fait_pas:
      "il ne rouvre pas un manque signalé que ce dépôt avait fermé, et ne prévient personne : il efface une saisie et remet la quantité là d'où elle venait",
    // Des articles et des logements réels (revue adverse M4) : « serviettes »,
    // « à Cocon » (deux logements) et « au Nid » sont partis.
    dit_comme: [
      "Retire le dépôt de gel douche d'hier à Soleil du Sud, c'est une erreur",
      "Ce dépôt de papier toilette est en double, enlève-le",
      "Efface le dépôt de lessive du 12 janvier au Cocon Naturel",
    ],
    vague: 5,
    motif_requis: false,
    autonome: false,
  },
});

module.exports = { LEVIERS_STOCK };
